import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Card } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead'; // Import Typeahead
import 'react-bootstrap-typeahead/css/Typeahead.css'; // Import Typeahead CSS
import { getServizio, updateServizio, createServizio, getSuggerimentiCategorieServizi, getSuggerimentiSupportiServizi } from '../services/api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTornaIndietro } from '../common/navigazione';

const ServiziEdit = () => {
  const initialFormData = {
    sku: '',
    descrizione: '',
    prezzo: '',
    iva: 22,
    costo: '',
    categoria: '',
    supporto: '',
    titolo_servizio: '',
    immagine_url: '',
    prezzo_indicativo_da: '',
    unita_misura_base: '',
    costo_unitario_base: '',
    opzioni_configurabili_json: '[]',
    attivo: true,
    pubblica_su_web: true,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [categorieSuggestions, setCategorieSuggestions] = useState([]); // Stato per i suggerimenti di categoria
  const [supportiSuggestions, setSupportiSuggestions] = useState([]); // Stato per i suggerimenti di supporto
  const navigate = useNavigate();
  const tornaIndietro = useTornaIndietro('/servizi');
  const { id } = useParams();
  const location = useLocation();

  // Funzione unificata per gestire la selezione e l'inserimento in Typeahead
  const handleTypeaheadChange = (name, selected) => {
    // Se l'utente cancella l'input, selected sarà un array vuoto.
    if (selected.length === 0) {
      setFormData({
        ...formData,
        [name]: ''
      });
      return;
    }

    const selection = selected[0];
    // Se è una nuova voce (creata con 'allowNew'), l'oggetto avrà la forma { custom: true, label: 'testo' }
    // Se è una selezione esistente, sarà una stringa o un oggetto che la rappresenta.
    const value = selection.custom ? selection.label : (typeof selection === 'object' ? selection.label : selection);

    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Cattura anche il testo digitato senza selezionare la voce "New: ..." dal menu,
  // altrimenti un valore nuovo mai usato prima va perso al salvataggio.
  const handleTypeaheadInputChange = (name, text) => {
    setFormData({
      ...formData,
      [name]: text
    });
  };

  useEffect(() => {
    // Gestione anteprima immagine
    if (selectedFile) {
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreview(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (formData.immagine_url) {
      const url = formData.immagine_url;
      setPreview(url.startsWith('http') ? url : `${process.env.REACT_APP_STATIC_FILES_BASE_URL}${url}`);
      return;
    }
    setPreview(undefined);
  }, [selectedFile, formData.immagine_url]);

  useEffect(() => {
    // Caricamento suggerimenti categorie e supporti
    const fetchSuggestions = async () => {
      try {
        const categorieRes = await getSuggerimentiCategorieServizi();
        setCategorieSuggestions(categorieRes.data);
        const supportiRes = await getSuggerimentiSupportiServizi();
        setSupportiSuggestions(supportiRes.data);
      } catch (error) {
        console.error("Errore nel caricamento dei suggerimenti:", error);
      }
    };
    fetchSuggestions();

    // Caricamento dati servizio
    if (id) {
      const fetchServizio = async () => {
        try {
          const response = await getServizio(id);
          const servizioData = response.data;
          setFormData({
            ...servizioData,
            prezzo: servizioData.prezzo !== null ? servizioData.prezzo.toString() : '',
            iva: servizioData.iva !== null ? servizioData.iva.toString() : '22',
            costo: servizioData.costo !== null ? servizioData.costo.toString() : '',
            supporto: servizioData.supporto || '',
            prezzo_indicativo_da: servizioData.prezzo_indicativo_da !== null ? servizioData.prezzo_indicativo_da.toString() : '',
            costo_unitario_base: servizioData.costo_unitario_base !== null ? servizioData.costo_unitario_base.toString() : '',
            opzioni_configurabili_json: servizioData.opzioni_configurabili_json || '[]',
          });
        } catch (error) {
          console.error("Errore nel caricamento del servizio:", error);
          alert("Impossibile caricare i dati del servizio.");
          navigate('/servizi');
        }
      };
      fetchServizio();
    } else {
      const queryParams = new URLSearchParams(location.search);
      const duplicateId = queryParams.get('duplicateId');
      if (duplicateId) {
        const fetchServizioToDuplicate = async () => {
          try {
            const response = await getServizio(duplicateId);
            const servizioData = response.data;
            setFormData({
              ...servizioData,
              sku: '',
              titolo_servizio: servizioData.titolo_servizio ? `${servizioData.titolo_servizio} (Copia)` : '',
              prezzo: servizioData.prezzo !== null ? servizioData.prezzo.toString() : '',
              iva: servizioData.iva !== null ? servizioData.iva.toString() : '22',
              costo: servizioData.costo !== null ? servizioData.costo.toString() : '',
              supporto: servizioData.supporto || '',
              prezzo_indicativo_da: servizioData.prezzo_indicativo_da !== null ? servizioData.prezzo_indicativo_da.toString() : '',
              costo_unitario_base: servizioData.costo_unitario_base !== null ? servizioData.costo_unitario_base.toString() : '',
              opzioni_configurabili_json: servizioData.opzioni_configurabili_json || '[]',
            });
            navigate(location.pathname, { replace: true });
          } catch (error) {
            console.error("Errore durante il recupero del servizio per la duplicazione:", error);
            alert("Errore durante il recupero dei dati per la duplicazione.");
          }
        };
        fetchServizioToDuplicate();
      }
    }
  }, [id, navigate, location]);


  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Ignora i campi di tipo 'file' che vengono gestiti da handleFileChange
    if (type === 'file') return;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const dataToSubmit = new FormData();

    // Aggiungi tutti i campi del form al FormData
    Object.keys(formData).forEach(key => {
        // Gestisci i valori null o undefined per non inviarli come "null" o "undefined"
        if (formData[key] !== null && formData[key] !== undefined) {
            // Se è un valore booleano, convertilo in '0' o '1'
            if (typeof formData[key] === 'boolean') {
                dataToSubmit.append(key, formData[key] ? '1' : '0');
            } else {
                dataToSubmit.append(key, formData[key]);
            }
        }
    });

    // Aggiungi il file immagine se selezionato
    if (selectedFile) {
      dataToSubmit.append('immagine_file', selectedFile);
    }

    try {
      if (id) {
        await updateServizio(id, dataToSubmit); // Invia FormData per l'aggiornamento
        alert('Servizio aggiornato con successo!');
      } else {
        await createServizio(dataToSubmit); // Invia FormData per la creazione
        alert('Servizio creato con successo!');
      }
      navigate('/servizi');
    } catch (error) {
      console.error(`Errore ${id ? 'nell\'aggiornamento' : 'nella creazione'} del servizio:`, error.response || error);
      alert(`Errore ${id ? 'nell\'aggiornamento' : 'nella creazione'} del servizio.`);
    }
  };

  return (
    <Container>
      <Row className="align-items-center mb-3">
        <Col>
          <h2>{id ? 'Modifica Servizio' : 'Nuovo Servizio'}</h2>
        </Col>
        <Col xs="auto" className="ms-auto">
          <Button variant="secondary" onClick={tornaIndietro} className="me-2">
            Torna alla Lista
          </Button>
          <Button variant="primary" type="submit" form="servizio-form">
            {id ? 'Salva Modifiche' : 'Salva Nuovo Servizio'}
          </Button>
        </Col>
      </Row>
      <Form id="servizio-form" onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>Dettagli Servizio</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSku">
                  <Form.Label>SKU</Form.Label>
                  <Form.Control type="text" name="sku" value={formData.sku} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formCategoria">
                  <Form.Label>Categoria</Form.Label>
                  <Typeahead
                    id="categoria-typeahead"
                    options={categorieSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('categoria', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('categoria', text)}
                    selected={formData.categoria ? [formData.categoria] : []}
                    placeholder="Seleziona o inserisci una categoria"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formSupporto">
                  <Form.Label>Supporto</Form.Label>
                  <Typeahead
                    id="supporto-typeahead"
                    options={supportiSuggestions}
                    allowNew
                    onChange={(selected) => handleTypeaheadChange('supporto', selected)}
                    onInputChange={(text) => handleTypeaheadInputChange('supporto', text)}
                    selected={formData.supporto ? [formData.supporto] : []}
                    placeholder="Seleziona o inserisci un supporto"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formTitoloServizio">
                  <Form.Label>Titolo Servizio</Form.Label>
                  <Form.Control type="text" name="titolo_servizio" value={formData.titolo_servizio} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Form.Group controlId="formImmagineFile" className="mb-3">
                    <Form.Label>Carica Nuova Immagine</Form.Label>
                    <Form.Control type="file" onChange={handleFileChange} />
                </Form.Group>
                {/* Campo nascosto per mantenere il valore di immagine_url se non viene caricata una nuova immagine */}
                <input type="hidden" name="immagine_url" value={formData.immagine_url || ''} />

              </Col>
              <Col md={4}>
                <Form.Label>Anteprima</Form.Label>
                <div className="text-center border p-2" style={{ minHeight: '150px' }}>
                  {preview ? (
                    <img src={preview} alt="Anteprima" className="img-fluid" />
                  ) : (
                    <span>Nessuna immagine</span>
                  )}
                </div>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="formDescrizione">
              <Form.Label>Descrizione</Form.Label>
              <Form.Control as="textarea" rows={3} name="descrizione" value={formData.descrizione} onChange={handleChange} required />
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Dettagli Preventivo</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formPrezzoIndicativoDa">
                  <Form.Label>Prezzo Indicativo "Da"</Form.Label>
                  <Form.Control type="number" step="0.01" name="prezzo_indicativo_da" value={formData.prezzo_indicativo_da} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formUnitaMisuraBase">
                  <Form.Label>Unità di Misura Base</Form.Label>
                  <Form.Control type="text" name="unita_misura_base" value={formData.unita_misura_base} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formCostoUnitarioBase">
                  <Form.Label>Costo Unitario Base</Form.Label>
                  <Form.Control type="number" step="0.01" name="costo_unitario_base" value={formData.costo_unitario_base} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formAttivo">
                  <Form.Check
                    type="checkbox"
                    label="Attivo"
                    name="attivo"
                    checked={formData.attivo}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formPubblicaSuWeb">
                  <Form.Check
                    type="checkbox"
                    label="Pubblica su Web"
                    name="pubblica_su_web"
                    checked={formData.pubblica_su_web}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3" controlId="formOpzioniConfigurabiliJson">
              <Form.Label>Opzioni Configurabili (JSON)</Form.Label>
              <Form.Control as="textarea" rows={5} name="opzioni_configurabili_json" value={formData.opzioni_configurabili_json} onChange={handleChange} />
            </Form.Group>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Header>Prezzi e Quantità</Card.Header>
          <Card.Body>
            <Row><Col><Form.Group className="mb-3" controlId="formPrezzo"><Form.Label>Prezzo</Form.Label><Form.Control type="number" step="0.01" name="prezzo" value={formData.prezzo} onChange={handleChange} /></Form.Group></Col>
              <Col><Form.Group className="mb-3" controlId="formCosto"><Form.Label>Costo</Form.Label><Form.Control type="number" step="0.01" name="costo" value={formData.costo} onChange={handleChange} /></Form.Group></Col>
              <Col><Form.Group className="mb-3" controlId="formIva"><Form.Label>IVA %</Form.Label><Form.Control type="number" step="0.01" name="iva" value={formData.iva} onChange={handleChange} /></Form.Group></Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>
    </Container>
  );
};

export default ServiziEdit; // Modificato: Export ServiziEdit